/**
 * IntakeSectionRenderer - Renders a section with CollapsibleSection and dynamic grid layout
 */

import React from 'react';
import {
  CollapsibleSection,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { IntakeFieldRenderer } from './IntakeFieldRenderer';
import { AiExtractedInsuranceSuggestion } from './AiExtractedInsuranceSuggestion';
import type {
  IntakeSectionDefinition,
  IntakeFieldDefinition,
} from '../../config/intakeFormSchema';
import type { IntakeResponse } from '../../services/api/types.gen';
import type { IntakeRecord } from '../../store/slices/intakeSlice';

// =============================================================================
// Types
// =============================================================================

export interface IntakeSectionRendererProps {
  sectionKey: string;
  section: IntakeSectionDefinition;
  formData: Partial<IntakeResponse>;
  validationErrors: Record<string, string | null>;
  onFieldChange: (fieldId: string, value: unknown, fieldType?: string) => void;
  onFieldBlur: (field: IntakeFieldDefinition, newValue?: unknown) => void;
  showAIExplanations?: boolean;
  // For dropdown fields with dynamic options
  agencyInsuranceOptions?: Array<{ value: string; label: string }>;
  agencyInsurancesLoading?: boolean;
  agencyInsurancesError?: string | null;
  agencyClinicOptions?: Array<{ value: string; label: string }>;
  agencyClinicsLoading?: boolean;
  agencyClinicsError?: string | null;
  // For accessing intake record data
  selectedIntake?: IntakeResponse | IntakeRecord | null;
  // Upload name -> display name for intake documents (e.g. for PDF references)
  documentNameDict?: Record<string, string>;
  /** Fields the user has edited in this session. When a dirty field is empty,
   *  resolveFieldValue returns the empty value instead of falling back to
   *  the AI-extracted JSONB value. */
  dirtyFields?: Set<string>;
}

// =============================================================================
// Grid column mapping for Tailwind
// =============================================================================

const gridColsClasses: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const colSpanClasses: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

const isEmptyFieldValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const resolveFieldValue = (
  formData: Partial<IntakeResponse>,
  fieldId: string,
  dirtyFields?: Set<string>
): unknown => {
  const topLevelValue = formData[fieldId as keyof IntakeResponse] as unknown;
  const jsonbValue = formData.fields?.[fieldId];

  // If the user has edited this field in the current session, trust the
  // top-level value as the source of truth — even if it's empty.
  if (dirtyFields?.has(fieldId)) {
    return topLevelValue;
  }

  // For unedited fields, prefer JSONB value when top-level is an empty default
  // (e.g. ensureRequiredFields converted null → "").
  if (isEmptyFieldValue(topLevelValue) && jsonbValue !== undefined) {
    return jsonbValue;
  }

  return topLevelValue ?? jsonbValue;
};

// =============================================================================
// Component
// =============================================================================

export const IntakeSectionRenderer: React.FC<IntakeSectionRendererProps> = ({
  section,
  formData,
  validationErrors,
  onFieldChange,
  onFieldBlur,
  showAIExplanations = false,
  agencyInsuranceOptions,
  agencyInsurancesLoading,
  agencyInsurancesError,
  agencyClinicOptions,
  agencyClinicsLoading,
  agencyClinicsError,
  selectedIntake,
  documentNameDict,
  dirtyFields,
}) => {
  const gridCols = gridColsClasses[section.display.columns] || 'grid-cols-4';

  return (
    <CollapsibleSection title={section.section_name}>
      <div className={`grid ${gridCols} gap-3`}>
        {section.content.map(field => {
          const colSpan = colSpanClasses[field.columns || 1] || 'col-span-1';
          const value = resolveFieldValue(
            formData,
            field.field_id,
            dirtyFields
          );
          const error = validationErrors[field.field_id] || undefined;
          const warning = field.required && !value;

          // Skip object_array fields (no longer supported)
          if (field.type === 'object_array') {
            return null;
          }

          // Conditionally hide prior_hha if has_transferred_from_another_hha is not true
          if (field.field_id === 'prior_hha') {
            const transferredFromHha =
              (formData.has_transferred_from_another_hha as
                | boolean
                | null
                | undefined) ??
              (formData.fields?.has_transferred_from_another_hha as
                | boolean
                | null
                | undefined);
            if (transferredFromHha !== true) {
              return null;
            }
          }

          // For dropdown with dynamic source - agency insurance
          if (
            field.type === 'dropdown' &&
            field.dropdown_source === 'agency_insurance'
          ) {
            return (
              <div key={field.field_id} className={colSpan}>
                <VirtualizedFormSelect
                  label={field.label}
                  placeholder={
                    agencyInsurancesLoading
                      ? 'Loading agency insurances...'
                      : agencyInsurancesError
                        ? 'Error loading agency insurances'
                        : 'Select agency insurance'
                  }
                  options={agencyInsuranceOptions || []}
                  required={field.required}
                  disabled={agencyInsurancesLoading}
                  error={error}
                  name={field.field_id}
                  defaultValue={(value as string) || ''}
                  onChange={e => {
                    onFieldChange(field.field_id, e.target.value, field.type);
                  }}
                />
                <div className='text-xs text-gray-500 mt-1'>
                  ✓ = supports eligibility checks
                </div>
                {selectedIntake?.primary_insurance && (
                  <AiExtractedInsuranceSuggestion
                    value={selectedIntake?.primary_insurance}
                  />
                )}
              </div>
            );
          }

          // For dropdown with dynamic source - agency clinic
          if (
            field.type === 'dropdown' &&
            field.dropdown_source === 'agency_clinic'
          ) {
            return (
              <div key={field.field_id} className={colSpan}>
                <IntakeFieldRenderer
                  field={field}
                  value={value}
                  onChange={newValue =>
                    onFieldChange(field.field_id, newValue, field.type)
                  }
                  onBlur={newValue => onFieldBlur(field, newValue)}
                  error={error}
                  warning={warning}
                  showAIExplanations={showAIExplanations}
                  dropdownOptions={agencyClinicOptions}
                  dropdownLoading={agencyClinicsLoading}
                  dropdownPlaceholder={
                    agencyClinicsLoading
                      ? 'Loading clinics...'
                      : agencyClinicsError
                        ? 'Error loading clinics'
                        : 'Select clinic'
                  }
                  fieldExplanation={
                    formData.fields_explanations?.[field.field_id] || null
                  }
                  documentNameDict={documentNameDict}
                />
              </div>
            );
          }

          return (
            <div key={field.field_id} className={colSpan}>
              <IntakeFieldRenderer
                field={field}
                value={value}
                onChange={newValue =>
                  onFieldChange(field.field_id, newValue, field.type)
                }
                onBlur={newValue => onFieldBlur(field, newValue)}
                error={error}
                warning={warning}
                showAIExplanations={showAIExplanations}
                fieldExplanation={
                  formData.fields_explanations?.[field.field_id] || null
                }
                documentNameDict={documentNameDict}
              />
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
};
